import React from 'react';
import './index.css';
import { createRoot } from 'react-dom/client';

import { resolveSsoBootstrap } from '@utils/ssoBootstrap';
import { watchDocumentUnloading } from '@utils/isNetworkError';

// Reload, tab close, or any external navigation kills in-flight requests with
// a network-error shape; the mark keeps them from surfacing as bogus
// "Connection Failed" toasts while the page is deliberately leaving.
watchDocumentUnloading();

async function enableMocking() {
  if (process.env.REACT_APP_MSW === 'on') {
    const { worker } = await import('./mocks/browser');
    await worker.start({ onUnhandledRequest: 'warn' });
  }
}

// Microsoft SSO returns to the frontend ROOT carrying `?sso_ticket=` (or
// `?sso_error=`). This has to be read before React Router mounts: `/` sits behind
// PrivateRoute, which redirects to /login the instant it sees an empty token and
// would drop the ticket unread. Called first so the parameters leave the address
// bar synchronously; resolves to null on every ordinary page load.
const ssoBootstrapPromise = resolveSsoBootstrap();

const container = document.getElementById('root');
const root = createRoot(container);

Promise.all([enableMocking(), ssoBootstrapPromise]).then(async ([, ssoBootstrap]) => {
  // App is imported lazily, AFTER the SSO handshake settled: the SSO popup's
  // whole job is one postMessage in resolveSsoBootstrap before window.close(),
  // and a static import would make it evaluate the entire application graph —
  // every MobX store, two SharedWorkers, the axios interceptors — just to
  // throw it away. A closing popup never reaches this line at all. Until React
  // mounts, the static preloader markup in public/index.html covers the wait.
  const { default: App } = await import('./app/App');

  root.render(<App ssoBootstrap={ ssoBootstrap }/>);
});
